import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { ReportingSport } from './types'

export type ExportConfig = {
  sessionId: string
  sessionDate: string
  sessionName: string
  sport: ReportingSport
}

type ReportingState = {
  teamId: string | null
  sessionId: string | null
  exportConfig: ExportConfig | null
  // True while the user is exporting the whole-session PDF. The reporting
  // app's <SessionWholeExport/> watches this flag, mounts the combined
  // print layout, and calls window.print(). Cleared by onafterprint.
  printingWholeSession: boolean
}

const initialState: ReportingState = {
  teamId: null,
  sessionId: null,
  exportConfig: null,
  printingWholeSession: false
}

// URL sync is handled by useReportingUrlWriteBack in the reporting app — the
// slice stays a pure state store. Writing URL from the reducer via
// history.replaceState bypasses react-router's internal listener, which then
// causes `useSearchParams(prev)` inside views to read stale params and clobber
// our updates.
export const reportingSlice = createSlice({
  name: 'reporting',
  initialState,
  reducers: {
    setReportingTeamId: (state, action: PayloadAction<string | null>) => {
      state.teamId = action.payload
    },
    setReportingSessionId: (state, action: PayloadAction<string | null>) => {
      state.sessionId = action.payload
    },
    setReportingExportConfig: (
      state,
      action: PayloadAction<ExportConfig | null>
    ) => {
      state.exportConfig = action.payload
    },
    startWholeSessionPrint: (state) => {
      state.printingWholeSession = true
    },
    finishWholeSessionPrint: (state) => {
      state.printingWholeSession = false
    }
  }
})

export const reducer = reportingSlice.reducer

export const {
  setReportingTeamId,
  setReportingSessionId,
  setReportingExportConfig,
  startWholeSessionPrint,
  finishWholeSessionPrint
} = reportingSlice.actions
